/*
 * product-model-ijewel.js — <ijewel-viewer> custom element (boot + lifecycle).
 *
 * Boots ONE headless WebGI viewer into itself, exposes it as
 * window.iJewelActiveViewer, and fires `ijewel:viewer-ready`. It knows nothing
 * about variants — model selection lives in the decoupled bridge
 * (product-ijewel-bridge.js), keeping the base variant engine untouched.
 *
 * Public API (unchanged): window.iJewelSDKSettings, window.iJewelActiveViewer,
 * window.__ijewelRuntimePromise, and the `ijewel:viewer-ready` CustomEvent.
 * connectedCallback boots (viewport-gated + cancellable); disconnectedCallback
 * disposes the viewer, removes injected chrome-hiding CSS, and fires
 * `ijewel:viewer-destroyed` — so Shopify section re-renders clean up for free.
 */
// @ts-nocheck — leans on the WebGI runtime (CoreViewerApp) and custom window.*
// globals the editor's implicit JS type-checker can't resolve. No tsconfig ships.

const DEFAULT_SDK_SETTINGS = {
  // WebGI 0.22 (Threepipe-based engine) renders diamonds with noticeably crisper
  // facet contrast / refractions than the old 0.15 core. It ships as a single
  // bundle at a different path than the legacy `viewer-<ver>.js` runtime; the
  // full iJewel-CDN URL is allow-listed by loadViewerRuntime() below and by the
  // head preload (product-ijewel-preload.liquid) — keep both in sync.
  webgi_version: 'https://releases.ijewel3d.com/libs/webgi-v0/bundle-0.22.0.js',
  scene_settings: '',
  environment_map: '',
  // Cosmetic loading-screen settings (merchant-overridable via the
  // ijewel3d_settings shop metafield). Behaviour switches — what shows when —
  // live in LOADING_SCREEN_BEHAVIOUR below and always win, so no metafield can
  // re-enable developer chrome. `logoImage` is deliberately ABSENT: each GLB's
  // embedded viewer config supplies the client's own monogram artwork, and
  // writing any value here would overwrite it on every re-assert.
  loading: {
    loadingTextHeader: 'Loading 3D Experience',
    backgroundOpacity: 0,
    background: '#ffffff',
    textColor: '#222222',
  },
};

// Everything the SDK renders that the theme drives itself, in one place.
const HIDDEN_SDK_CHROME = {
  uiPlugins: [
    'VariationConfiguratorGridUiPlugin',
    'VariationConfiguratorUiPlugin',
    'ConfiguratorUiPlugin',
    'ConfiguratorGridUiPlugin',
    'MaterialConfiguratorUiPlugin',
  ],
  selectors: [
    '.webgi-ui-container',
    '.wgi-configurator-container',
    '.wgi-configurator-ui',
    '.customContextGrid',
    '.customContextGridHeading',
    '.ijewel-viewer-container [class*="customContext"]',
    '.ijewel-logo',
    /* Kill third-party branding images (the WebGI/iJewel logos) without
       killing our own: this style lands in document.head, so a bare
       img[src*="logo"] used to hide EVERY matching image on the page —
       including the loading screen's monogram, whose asset is named
       ijewel-loading-logo.svg. Scope it to the viewer container and exempt
       the LoadingScreenPlugin's own logo element (which the theme points at
       the client's monogram — see LOADING_SCREEN_BEHAVIOUR.logoImage). */
    '.ijewel-viewer-container img[src*="logo"]:not(.loadingScreenLogoImage)',
  ],
  hideDeclaration: '{display:none !important;opacity:0 !important;pointer-events:none !important;}',
};

// Viewport gate for the boot opt-out and the desktop-only wrapper.
const MOBILE_MEDIA_QUERY = '(max-width: 767.98px)';
// InteractionPromptPlugin's idle hint swings the camera by rotationDistance
// radians of azimuth (WebGI default 0.3). Lower = subtler left/right sway.
const PROMPT_ROTATION_DISTANCE = 0.08;

/* ------------------------------------------------------------------------
   Interaction policy (zoom-to-cursor + pan, bounded natively)

   The zoom focus itself is never ours to set: `zoomToCursor` stays on and the
   SDK aims each dolly at the live pointer. Everything here is the SAFETY CAGE
   around that, and it is built only from WebGI/three OrbitControls properties
   the SDK applies inside its own update loop — no `change` listener of ours,
   no camera correction of ours.

   The cage has two halves, and they take their sizes from different places:

     - the allowed-target SPHERE is sized and centred on the model's bounding
       box (see computeTargetBound);
     - the DISTANCE limits come from the GLB's authored camera pose, because
       these GLBs ship pre-framed on a detail and the old code sized them off
       the whole model instead — which is what left a pendant-framed necklace
       with an 18% dolly range on mobile.

   The old leash failed for a third reason, fixed by both halves together: it
   centred a bbox-centred bound with a radius (61.1) SMALLER than the authored
   target's own offset from that centre (60.0), so a necklace shopper began
   1.1 units from the wall and zoom-to-cursor fought it from the first scroll.
   ------------------------------------------------------------------------ */

/* The allowed-target sphere is centred on the model's BBOX CENTRE, with a
   radius of the model's LARGEST half-extent. Both choices were measured on
   the live necklace GLB (110 x 171 x 5.4, authored target 60 below centre),
   panning aggressively in every direction at deep zoom:

     old leash      centre (0,0,0)   r 61.1   drift at rest 60.0 -> 1.1 units
                    of headroom, so zoom-to-cursor fought it from frame one.
     authored centre (0,-60,0) r 155.7  reached 130 units BELOW the piece,
                    where there is no geometry: panning parked the frame on
                    empty space at x=-125.5 with the model ending at x=-55.
     half-diagonal  centre (0,0,0)   r 101.8  still overshot the model's own
                    span by 16 units vertically — enough to lose a thin
                    pendant off the bottom of the frame at max zoom.
     THIS           centre (0,0,0)   r 85.7 (= half of the 171 y-extent), so
                    along the model's dominant axis — the one shoppers pan a
                    necklace or a chain down — the target can never leave the
                    piece at all. Drift at rest is 60.0, i.e. 25.7 of headroom,
                    and both the pendant and the top of the chain stay
                    reachable.

   The authored pose is NOT the sphere centre (a pendant-framed GLB would put
   the model at the edge of its own safe region). It drives the distance limits
   below, and floors this radius for any GLB framed outside its own bbox. */
const TARGET_RADIUS_MARGIN = 1.0;
// A GLB framed outside its own bounds must not start against the wall.
const AUTHORED_OFFSET_MARGIN = 1.15;
// Degenerate/missing bounds must not lock the target to a point: never allow
// less than this fraction of the authored distance.
const TARGET_RADIUS_FLOOR_RATIO = 0.15;

/* Zoom range, as fractions of the AUTHORED rest distance. The old code took
   these from getFittingDistance(wholeModel) x 1.45, which on a pendant-framed
   necklace put the floor at 197 against a rest distance of 240 — an 18% dolly,
   i.e. pinch zoom did essentially nothing on mobile.

   0.20 was picked against the renders, not in the abstract:
     0.12 -> ring floor 8.2, inside its own 22.5-deep band: the frame fills
             with out-of-focus gold and orbiting sweeps through the metal.
     0.20 -> ring floor 13.7: a clean macro with the band, prongs, stone and
             the hallmark engraving all legible. Necklace floor 48 (a 21-unit
             frame) fills with the pendant; the tennis necklace gets ~8 links
             across the frame.
   Still far tighter than the iJewel default of an absolute 0.35. Tune here. */
const MIN_DISTANCE_RATIO = 0.2;
const MAX_DISTANCE_RATIO = 2.5;

class IjewelViewer extends HTMLElement {
  #viewer = null;
  #chromeStyle = null;
  #promptObserver = null;
  #promptRefreshHandler = null;
  #promptFrameHandler = null;
  #promptStartTimer = null;
  #controller = null;
  #observer = null;
  #controlsPolicyHandler = null;
  #authoredPoseHandler = null;
  #variantUpdateHandler = null;
  #loadingSettingsHandler = null;
  #swapLoadingHandler = null;

  connectedCallback() {
    window.iJewelSDKSettings = { ...DEFAULT_SDK_SETTINGS, ...(window.iJewelSDKSettings || {}) };

    /* Product-group opt-out: when a Product Group has "Disable 3D viewer on
       mobile" set, product-main-gallery.liquid renders the viewer inside a
       [data-ijewel-desktop-only] wrapper that CSS hides below 768px, and
       renders the standard variant image gallery for phones.

       Hiding it in CSS is not enough — the element still upgrades, still
       boots a WebGL context and still downloads the GLB. On the graduated
       tennis necklace that is 8.5MB and 1.68M triangles per carat option,
       which is the reported mobile Safari crash. So bail out entirely here.

       Deliberately NOT re-checked on resize: a phone that rotates or opens a
       tablet-width split view should not suddenly boot a viewer mid-session,
       and a desktop browser narrowed past 768px keeps the viewer it already
       has. The breakpoint matches IS_MOBILE in product-ijewel-bridge.js. */
    if (isMobileViewport() && this.closest('[data-ijewel-desktop-only]')) return;

    this.#watchViewport();
  }

  disconnectedCallback() {
    this.#destroy();
  }

  // The initialized viewer, for a bridge that prefers awaiting over the event.
  get viewer() {
    return this.#viewer;
  }

  // Boot once the viewer is near the viewport. Do NOT abort on brief
  // non-intersection: Mobile Safari address-bar show/hide and soft chrome
  // resize can flip isIntersecting mid-init, and cancelling left a dead
  // WebGL canvas + "never loads" until a lucky retry. Section unload still
  // cancels via disconnectedCallback → #destroy.
  #watchViewport() {
    const rootMargin = isMobileViewport() ? '400px' : '200px';
    this.#observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || this.#controller || this.#viewer) return;

        this.#controller = new AbortController();
        this.#boot(this.#controller.signal).catch((error) => {
          console.error('iJewel 3D: viewer failed to initialize —', error);
          // Allow a later re-intersection to retry a failed boot.
          this.#controller = null;
        });
      },
      { rootMargin }
    );
    this.#observer.observe(this);
  }

  async #boot(signal) {
    await loadViewerRuntime();
    if (this.#cancelled(signal)) return;

    const viewer = new window.CoreViewerApp({ container: this });
    // Bloom is required for diamond facet contrast — without it white
    // diamonds read milky/frosted even when DiamondPlugin prep is correct.
    // Same on mobile + desktop so render quality matches.
    await viewer.initialize({
      caching: true,
      ground: true,
      bloom: true,
      enableDrop: false,
      importPopup: false,
      debug: false,
    });
    if (this.#cancelled(signal)) return disposeViewer(viewer);

    applyRenderScale(viewer);
    this.#hideSdkChrome(viewer);
    this.#configureInteractionPrompt(viewer);
    applyEncryptionHook(viewer);
    // Registers the native GLB loading screen (first-load only) and applies the
    // customer-safe settings. Each GLB's embedded config overwrites plugin
    // properties on every model load, so re-assert on the bridge's load events.
    applyLoadingSettings(viewer);
    this.#loadingSettingsHandler = () => applyLoadingSettings(viewer);
    LOADING_SCREEN_REASSERT_EVENTS.forEach((name) =>
      document.addEventListener(name, this.#loadingSettingsHandler)
    );
    // Cover foreground swap loads too: open the files-loading window when the
    // bridge starts a swap; the re-asserts above close it after staging.
    this.#swapLoadingHandler = () => enableSwapLoadingScreen(viewer);
    document.addEventListener('ijewel:model-swap-start', this.#swapLoadingHandler);
    await applyEnvironment(viewer);
    // Detached/cancelled while the environment loaded → unwind, don't expose.
    if (this.#cancelled(signal)) return this.#destroy(viewer);

    // WebGI 0.22 registers Bloom when initialize({ bloom: true }) but leaves
    // `enabled: false` until a scene-settings .vjson flips it. We ship without
    // scene_settings on this store, so force it on here (and the bridge
    // re-asserts after gem/metal applies that can clear the flag).
    applyBloom(viewer);

    /* Interaction policy: zoom-to-cursor + pan, bounded by native
       OrbitControls constraints anchored on the GLB's authored camera pose.

       Two separate wirings, and the split matters:

       - `ijewel:model-swap-staged` fires inside the bridge's swapModel AFTER
         the new GLB's exported camera has been applied but BEFORE the bridge
         restores the shopper's own pose (product-ijewel-bridge.js:1146-1186).
         That is the only window in which the authored target is readable, so
         it is where the anchor is captured. Metal-only swaps take the bridge's
         same-cad short-circuit and emit no swap events at all, so the anchor —
         and the shopper's camera — are correctly left alone.

       - model-ready / model-swap-done / variant:update re-APPLY the policy
         without recapturing, because each GLB import builds fresh
         OrbitControls that come back at the SDK/GLB defaults. */
    this.#authoredPoseHandler = () => captureAuthoredPose(viewer);
    document.addEventListener('ijewel:model-swap-staged', this.#authoredPoseHandler);

    applyInteractionPolicy(viewer);
    this.#controlsPolicyHandler = () => {
      // First load can reach model-ready without a staged capture (cache-hit
      // paths); the camera is still the authored one there, so seed from it.
      if (!authoredPoses.has(viewer)) captureAuthoredPose(viewer);
      applyInteractionPolicy(viewer);
    };
    document.addEventListener('ijewel:model-ready', this.#controlsPolicyHandler);
    document.addEventListener('ijewel:model-swap-done', this.#controlsPolicyHandler);

    // Configurator/gem/metal changes can rebuild controls without a geometry
    // swap. variant:update fires before the bridge has finished, so debounce
    // onto the settled scene — same 500ms the old mobile clamp refresh used.
    let policyRefreshTimer = null;
    this.#variantUpdateHandler = () => {
      clearTimeout(policyRefreshTimer);
      policyRefreshTimer = setTimeout(() => {
        requestAnimationFrame(() => applyInteractionPolicy(viewer));
      }, 500);
    };
    document.addEventListener('variant:update', this.#variantUpdateHandler);

    this.#viewer = viewer;
    window.iJewelActiveViewer = viewer;
    this.dataset.ijewelReady = 'true';
    window.dispatchEvent(
      new CustomEvent('ijewel:viewer-ready', { detail: { container: this, viewer } })
    );
    // Boot done — a completed viewer must survive the shopper scrolling away.
    this.#observer?.disconnect();
  }

  #cancelled(signal) {
    return signal.aborted || !this.isConnected;
  }

  // Force the SDK headless — the theme drives the UI, not WebGI.
  #hideSdkChrome(viewer) {
    HIDDEN_SDK_CHROME.uiPlugins.forEach((name) => {
      const plugin = viewer.getPluginByType(name);
      if (!plugin) return;
      // Prefer WebGI's reason-based disable over a bare `enabled = false`.
      // A reason-based disable survives SDK reinitialization better than a
      // plain `enabled = false` assignment.
      if (typeof plugin.disable === 'function') plugin.disable('theme-headless');
      else plugin.enabled = false;
      if (plugin.ui?.container) plugin.ui.container.style.display = 'none';
    });

    neutralizeConfiguratorPreviews(viewer);

    const style = document.createElement('style');
    style.textContent = HIDDEN_SDK_CHROME.selectors.join(',') + HIDDEN_SDK_CHROME.hideDeclaration;
    document.head.appendChild(style);
    this.#chromeStyle = style;
  }

  // Keep WebGI's motion cue while removing its pointer/orbit artwork. The
  // plugin is created from the GLB's embedded viewer config, after viewer boot,
  // so settings must also be applied when each staged model becomes ready.
  #configureInteractionPrompt(viewer) {
    const applySettings = () => {
      const plugin = viewer.getPluginByType('InteractionPromptPlugin');
      if (!plugin) {
        if (!this.#promptFrameHandler) {
          this.#promptFrameHandler = () => {
            if (!viewer.getPluginByType('InteractionPromptPlugin')) return;
            viewer.removeEventListener('postFrame', this.#promptFrameHandler);
            this.#promptFrameHandler = null;
            applySettings();
          };
          viewer.addEventListener('postFrame', this.#promptFrameHandler);
        }
        return;
      }

      plugin.enabled = true;
      plugin.enable?.('theme-headless');
      // Re-asserted on every model-ready/swap-done because the plugin is
      // (re)configured from each GLB's embedded viewer config.
      plugin.rotationDistance = PROMPT_ROTATION_DISTANCE;

      plugin.pointerIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1" viewBox="0 0 1 1" aria-hidden="true"></svg>';
      const hidePointer = () => {
        if (plugin.cursorEl?.style) {
          plugin.cursorEl.style.setProperty('display', 'none', 'important');
          plugin.cursorEl.style.setProperty('opacity', '0', 'important');
          plugin.cursorEl.style.setProperty('pointer-events', 'none', 'important');
        }
      };
      hidePointer();
      if (!this.#promptObserver) {
        this.#promptObserver = new MutationObserver(hidePointer);
        this.#promptObserver.observe(this, { childList: true, subtree: true });
      }

      clearTimeout(this.#promptStartTimer);
      this.#promptStartTimer = setTimeout(() => {
        this.#promptStartTimer = null;
        if (!plugin.animationRunning) plugin.startAnimation?.();
      }, 3000);

    };

    this.#promptRefreshHandler = applySettings;
    document.addEventListener('ijewel:model-ready', applySettings);
    document.addEventListener('ijewel:model-swap-done', applySettings);
    applySettings();
  }

  // Tear everything down. Runs on section unload (disconnectedCallback) and as
  // a mid-init unwind. Accepts an explicit viewer or falls back to the active.
  #destroy(viewer) {
    const target = viewer || this.#viewer;
    disposeViewer(target);

    if (this.#viewer === target) this.#viewer = null;
    if (window.iJewelActiveViewer === target) window.iJewelActiveViewer = null;

    this.#chromeStyle?.remove();
    this.#chromeStyle = null;
    if (this.#promptRefreshHandler) {
      document.removeEventListener('ijewel:model-ready', this.#promptRefreshHandler);
      document.removeEventListener('ijewel:model-swap-done', this.#promptRefreshHandler);
    }
    this.#promptRefreshHandler = null;
    if (this.#controlsPolicyHandler) {
      document.removeEventListener('ijewel:model-ready', this.#controlsPolicyHandler);
      document.removeEventListener('ijewel:model-swap-done', this.#controlsPolicyHandler);
    }
    this.#controlsPolicyHandler = null;
    if (this.#authoredPoseHandler) {
      document.removeEventListener('ijewel:model-swap-staged', this.#authoredPoseHandler);
    }
    this.#authoredPoseHandler = null;
    if (this.#variantUpdateHandler) {
      document.removeEventListener('variant:update', this.#variantUpdateHandler);
    }
    this.#variantUpdateHandler = null;
    if (target) authoredPoses.delete(target);
    if (this.#loadingSettingsHandler) {
      LOADING_SCREEN_REASSERT_EVENTS.forEach((name) =>
        document.removeEventListener(name, this.#loadingSettingsHandler)
      );
    }
    this.#loadingSettingsHandler = null;
    if (this.#swapLoadingHandler) {
      document.removeEventListener('ijewel:model-swap-start', this.#swapLoadingHandler);
    }
    this.#swapLoadingHandler = null;
    if (this.#promptFrameHandler) target?.removeEventListener?.('postFrame', this.#promptFrameHandler);
    this.#promptFrameHandler = null;
    clearTimeout(this.#promptStartTimer);
    this.#promptStartTimer = null;
    this.#promptObserver?.disconnect();
    this.#promptObserver = null;
    this.#observer?.disconnect();

    window.dispatchEvent(new CustomEvent('ijewel:viewer-destroyed'));
  }
}

if (!customElements.get('ijewel-viewer')) {
  customElements.define('ijewel-viewer', IjewelViewer);
}

// Lazy-load the WebGI runtime once; cache the promise globally.
function loadViewerRuntime() {
  if (window.CoreViewerApp) return Promise.resolve();
  if (window.__ijewelRuntimePromise) return window.__ijewelRuntimePromise;

  window.__ijewelRuntimePromise = new Promise((resolve, reject) => {
    const version = window.iJewelSDKSettings.webgi_version || '0.15.0';
    // Full-URL overrides are allow-listed to the iJewel CDN so a tampered
    // metafield can't inject a foreign script. Mirrors the resolution in
    // product-ijewel-preload.liquid — both must produce the SAME url or the
    // head preload goes unused.
    const url =
      version.indexOf('/') !== -1
        ? version.indexOf('https://releases.ijewel3d.com/') === 0
          ? version
          : 'https://releases.ijewel3d.com/webgi/runtime/viewer-0.15.0.js'
        : 'https://releases.ijewel3d.com/webgi/runtime/viewer-' + version + '.js';

    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load iJewel runtime: ' + url));
    document.head.appendChild(script);
  });

  return window.__ijewelRuntimePromise;
}

function disposeViewer(viewer) {
  if (typeof viewer?.dispose !== 'function') return;
  try {
    viewer.dispose();
  } catch (error) {
    debugWarn('viewer dispose failed', error);
  }
}

// Encrypted-model hook (iJewel docs). No-op unless a key/resolver is supplied.
function applyEncryptionHook(viewer) {
  const importer = viewer.getManager?.()?.importer;
  const registerFile = importer?.registerFile;
  const { encryption_key: fixedKey = '', encryption_key_resolver: keyResolver } =
    window.iJewelSDKSettings;

  if (!registerFile) return;
  if (!fixedKey && typeof keyResolver !== 'function') return;

  const preparser = registerFile.call(importer, 'temp.glb')?.preparsers?.[0];
  if (!preparser) return;

  preparser.key = (encryption, json) => {
    if (typeof keyResolver === 'function') {
      try {
        const dynamicKey = keyResolver(encryption, json);
        if (dynamicKey) return dynamicKey;
      } catch (resolverError) {
        debugWarn('encryption key resolver failed', resolverError);
      }
    }
    return fixedKey;
  };
}

// The GLB loading screen's behaviour switches. The WebGI 0.22 bundle SHIPS
// LoadingScreenPlugin but never registers it on the viewer, so this screen was
// off (and applyLoadingSettings dead code) until registerLoadingScreen below.
// The contract: show while the scene is empty (first load — client-approved
// monogram + progress bar), show again for FOREGROUND swap loads (a
// cache-miss shape change is a cold fetch+parse the shopper is waiting on —
// on disable_model_cache groups that is EVERY shape change), and never any
// developer chrome (defaults leak strings like
// "downloading: RG-5S-CLW-SIG-OV….glb?v=0 - 143%" to customers).
const LOADING_SCREEN_BEHAVIOUR = {
  showOnSceneEmpty: true, // drives the first-load show while nothing is staged
  /* RESTING state is false so the screen can never appear for BACKGROUND
     importer traffic — speculative pre-parses and idle pre-staging run through
     the same AssetImporter and would flash the screen over a perfectly good
     live model. enableSwapLoadingScreen() flips the live plugin property to
     true on `ijewel:model-swap-start` (a foreground, shopper-initiated load),
     and the post-swap re-asserts below restore this false, closing the window
     again. Cache-hit swaps import nothing, so the flag being briefly true
     shows no screen for them. */
  showOnFilesLoading: false,
  // Do NOT hide at files-load: the GLB's embedded config (which carries the
  // client's monogram logoImage) only applies once the GLB is parsed. Hiding
  // at files-load dismissed the screen BEFORE the monogram could render —
  // hold until the model object actually reaches the stage instead.
  hideOnFilesLoad: false,
  hideOnSceneObjectLoad: true, // fully hide once geometry is on stage…
  minimizeOnSceneObjectLoad: false, // …instead of minimizing to a corner chip
  showFileNames: false, // no GLB filenames
  showProcessStates: false, // no "downloading/processing" rows or percentages
  /* The plugin has NO progress bar element. showProgress only appends
     "- 47%" text to the per-file rows, which showFileNames:false removes —
     so it is inert here, and is pinned false so percentage text can never
     leak even if filenames are ever re-enabled. The visible horizontal bar
     is the theme's: product-ijewel-viewer.liquid restyles the plugin's
     spinner slot (.loadingScreenLoadingElement .loader) into a bar whose
     fill tracks --ijewel-load-progress, driven by wireLoadingProgress
     below from the same importer events the plugin listens to. */
  showProgress: false,

  /* The monogram, served from the theme rather than read out of the GLB.
     Each GLB does carry it — it is the ONE thing the client customised in
     their scene settings; every other LoadingScreenPlugin value in the file
     is an SDK default. But that config lives at
     scenes[0].extensions.WEBGI_viewer and is only applied via fromJSON once
     the GLB has downloaded AND parsed — which is precisely the window the
     loading screen exists to cover. So on a first load the screen rendered
     with no artwork until ~2.5s, by which point the model was arriving
     anyway.

     Setting it here means the monogram paints on the very first frame. The
     file is byte-identical to the artwork extracted from
     RG-5S-CLW-SIG-RD.glb, so when the GLB's own config lands mid-load it
     replaces this with the same image and nothing visibly changes. */
  logoImage: window.__ijewelLoadingLogoUrl || '',

  /* No text under the monogram. The client's reference is the monogram over
     a progress bar and nothing else; the SDK default is the literal string
     "Loading Files", which is developer chrome. Enforced here rather than in
     DEFAULT_SDK_SETTINGS so a metafield cannot put it back. */
  loadingTextHeader: '',
};

// Same document events the bridge fires around every model load — each GLB's
// embedded config is re-applied via fromJSON on EVERY load and overwrites
// theme-side writes, so settings must be re-asserted after each of these.
const LOADING_SCREEN_REASSERT_EVENTS = [
  'ijewel:model-ready',
  'ijewel:model-swap-staged',
  'ijewel:model-swap-visible',
  'ijewel:model-swap-done',
];

// Register the shipped-but-dormant LoadingScreenPlugin on this viewer.
// Returns the plugin instance or null; never throws (a failure here must not
// break the viewer — the theme's own is-loading overlay still covers boot).
function registerLoadingScreen(viewer) {
  try {
    let plugin = viewer.getPluginByType?.('LoadingScreenPlugin');
    if (plugin) return plugin;

    const Cls = window.LoadingScreenPlugin;
    if (typeof Cls !== 'function' || typeof viewer.getOrAddPluginSync !== 'function') return null;
    plugin = viewer.getOrAddPluginSync(Cls) || null;

    // The bundle's default logoImage is WebGI's own logo (static.webgi.xyz).
    // Blank it ONCE at registration — before any GLB config has applied — so
    // the first visible frames show no third-party branding. After this the
    // client's monogram arrives from the GLB's embedded config and is never
    // touched again (logoImage is excluded from every re-assert).
    if (plugin && /webgi\.xyz/.test(String(plugin.logoImage || ''))) plugin.logoImage = '';
    return plugin;
  } catch (error) {
    debugWarn('loading screen registration failed', error);
    return null;
  }
}

// Drives the theme's horizontal progress bar (the restyled spinner slot in
// the SDK loading screen — see product-ijewel-viewer.liquid). Listens to the
// same importer events LoadingScreenPlugin itself consumes and mirrors the
// aggregate the SDK's own AssetManagerLoadingBarPlugin computes, published as
// --ijewel-load-progress on the viewer container. Monotonic within a load
// burst so late-arriving files (materials queued behind the GLB) can't make
// the bar jump backwards; resets once the burst drains, by which point the
// screen is hidden (hideOnSceneObjectLoad) so nothing visible rewinds.
const PROGRESS_WIRED = new WeakSet();

function wireLoadingProgress(viewer) {
  if (PROGRESS_WIRED.has(viewer)) return;
  const importer = viewer.getManager?.()?.importer;
  const container = viewer.container;
  if (!importer || !container || typeof importer.addEventListener !== 'function') return;
  PROGRESS_WIRED.add(viewer);

  const inFlight = new Map();
  let floor = 0;
  importer.addEventListener('importFile', (event) => {
    const path = event?.path;
    if (!path) return;
    // Model files only. The environment map imports first, while the screen
    // is already showing — tracking it would fill the bar to 100% and reset
    // it before the GLB even starts, a visible double-fill glitch.
    if (!/\.glb(\?|#|$)/i.test(path)) return;
    if (event.state === 'done' || event.state === 'error') inFlight.delete(path);
    else inFlight.set(path, typeof event.progress === 'number' ? event.progress * 100 : undefined);

    let done = 0;
    let total = 0;
    inFlight.forEach((progress) => {
      done += progress ?? 99;
      total += 100;
    });
    let pct;
    if (total === 0) {
      pct = 100;
      floor = 0;
    } else {
      pct = Math.max(floor, Math.min(100, Math.round((done / total) * 100)));
      floor = pct;
    }
    container.style.setProperty('--ijewel-load-progress', pct + '%');
  });
}

/* The developer-chrome switches that must NEVER flip on while the screen is
   visible. With showOnFilesLoading enabled for swap windows the screen is now
   VISIBLE during model loads — exactly when each GLB's embedded (SDK-default)
   config re-applies via fromJSON. The event re-asserts land only AFTER staging,
   which leaves a visible window where "downloading: …glb - 47%" text could
   flash. Pin these keys with ignore-write instance accessors so no fromJSON
   (or any other writer) can flip them; the event re-asserts stay as backup. */
const PINNED_LOADING_KEYS = ['showFileNames', 'showProcessStates', 'showProgress', 'loadingTextHeader'];

function pinLoadingScreenBehaviour(plugin) {
  if (plugin.__ijewelBehaviourPinned) return;
  plugin.__ijewelBehaviourPinned = true;
  PINNED_LOADING_KEYS.forEach((key) => {
    if (!(key in plugin)) return;
    try {
      // Write once through the plugin's own path (side effects run), then pin.
      plugin[key] = LOADING_SCREEN_BEHAVIOUR[key];
      Object.defineProperty(plugin, key, {
        configurable: true,
        enumerable: true,
        get: () => LOADING_SCREEN_BEHAVIOUR[key],
        set: () => {},
      });
    } catch (_e) {
      /* best effort — the event re-asserts still cover this key */
    }
  });
}

/* Foreground swap load incoming (bridge fires `ijewel:model-swap-start` right
   before it fetches/parses the GLB). Open the screen's files-loading window so
   the cold fetch is covered; the post-swap re-asserts close it again. */
function enableSwapLoadingScreen(viewer) {
  try {
    const plugin = registerLoadingScreen(viewer);
    if (plugin) plugin.showOnFilesLoading = true;
  } catch (error) {
    debugWarn('swap loading screen enable failed', error);
  }
}

function applyLoadingSettings(viewer) {
  try {
    const plugin = registerLoadingScreen(viewer);
    if (!plugin) return;
    // Tells the bridge the SDK screen owns cold-swap loading UI, so it can
    // drop its own shimmer for those swaps (see freezeCanvas usage there).
    window.__ijewelSwapLoadingScreen = true;
    wireLoadingProgress(viewer);

    // Cosmetics first (merchant-overridable), behaviour last (always wins).
    // Only assign keys the plugin already exposes — never mint new properties.
    const settings = window.iJewelSDKSettings.loading;
    if (settings) {
      Object.keys(settings).forEach((key) => {
        if (key in plugin) plugin[key] = settings[key];
      });
    }
    Object.keys(LOADING_SCREEN_BEHAVIOUR).forEach((key) => {
      if (key in plugin) plugin[key] = LOADING_SCREEN_BEHAVIOUR[key];
    });

    // Once geometry exists the first-load case is over for this viewer's
    // lifetime. Turning showOnSceneEmpty off here means a swap that briefly
    // empties modelRoot (remove-then-add happens in swapModelStaged) can never
    // flash the screen back on.
    if (viewer.scene?.modelRoot?.children?.length > 0) plugin.showOnSceneEmpty = false;

    // Freeze the developer-chrome switches against fromJSON overwrites — must
    // run AFTER the behaviour loop above so the plugin's own setters have
    // applied the values once.
    pinLoadingScreenBehaviour(plugin);
  } catch (error) {
    debugWarn('loading screen settings failed', error);
  }
}

// The configurator data plugin (MaterialConfiguratorPlugin on the 0.22 engine,
// VariationConfiguratorPlugin on older builds) stays ENABLED — the bridge drives
// it to apply metal/gem variations. But every time its variation set changes it
// rebuilds its config UI (`_refreshUi`), and for each variation option that UI
// asks `_previewGenerator.generate()` to render the material onto a sphere and
// snapshot the full-resolution WebGL canvas via `canvas.toDataURL()`. On a 2x-DPR
// canvas that is ~25ms per option, and a ring with ~14 options makes it the
// single dominant cost of a first-time shape/metal swap: ~350ms of synchronous
// main-thread readback + PNG encode (measured via CPU-profile self-time and a
// wrapped `toDataURL`; call site `_refreshUi → ZO.generate → JO → toDataURL`).
//
// We render the configurator grid ourselves and hide WebGI's (see uiPlugins /
// selectors above), so these thumbnails are generated and then thrown away.
// Replace the generator with a no-op: variation APPLICATION (plugin.applyVariation)
// is untouched — only the never-shown preview image is skipped. Warmed re-swaps
// were already free (the plugin caches previews per material set); this makes the
// first view of each shape free too. The generator instance is created during
// viewer.initialize() and is stable for the viewer's lifetime, so patching once
// here holds.
function neutralizeConfiguratorPreviews(viewer) {
  ['MaterialConfiguratorPlugin', 'VariationConfiguratorPlugin'].forEach((name) => {
    const plugin = viewer.getPluginByType?.(name);
    const generator = plugin && plugin._previewGenerator;
    if (generator && typeof generator.generate === 'function') {
      generator.generate = () => '';
    }
  });
}

// Apply the merchant's viewer config: an optional scene-settings .vjson
// (tonemapping / exposure / post-processing — the iJewel viewer config the GLB
// import does NOT carry) and an optional environment HDR. These are independent:
// a store may ship one, both, or neither, so each is gated on its own value.
async function applyEnvironment(viewer) {
  const { environment_map: envMapPath, scene_settings: sceneSettings } = window.iJewelSDKSettings;
  if (!envMapPath && !sceneSettings) return;

  try {
    // Scene settings first: viewer.load() applies the full config (which can
    // itself set an environment); an explicit environment_map below then wins.
    if (sceneSettings) await viewer.load(sceneSettings);

    if (envMapPath) {
      const envMap = await viewer.getManager?.()?.importer?.importSinglePath(envMapPath);
      if (envMap) viewer.scene?.setEnvironment(envMap);
    }
  } catch (error) {
    debugWarn('environment/scene settings failed', error);
  }
}

// WebGI's RenderManager defaults renderScale to 1 device-independent pixel,
// so on a 2x/3x (retina-class) display the canvas backing buffer renders at
// 1x and the browser upscales it to fill the CSS box — visibly softer than
// the display's actual pixel density. Cap at 2 everywhere so retina phones
// match desktop sharpness without paying full 3x fill-rate on 3x iPhones.
function applyRenderScale(viewer) {
  const renderManager = viewer.renderManager;
  if (!renderManager || typeof renderManager.renderScale !== 'number') return;
  try {
    renderManager.renderScale = Math.min(window.devicePixelRatio || 1, 2);
  } catch (error) {
    debugWarn('render scale failed', error);
  }
}

// Ensure Bloom is actually running. initialize({ bloom: true }) only installs
// the pass on WebGI 0.22 — it does not set enabled=true without a scene-
// settings override.
function applyBloom(viewer) {
  try {
    const bloom = viewer.getPluginByType?.('Bloom') || viewer.getPluginByType?.('BloomPlugin');
    if (!bloom) return;
    bloom.enabled = true;
    if (typeof bloom.intensity === 'number' && bloom.intensity <= 0) bloom.intensity = 1;
  } catch (error) {
    debugWarn('bloom enable failed', error);
  }
}

// Bridge re-asserts bloom after variation applies (exposed for product-ijewel-bridge).
window.__ijewelEnsureDesktopBloom = function __ijewelEnsureDesktopBloom(viewer) {
  applyBloom(viewer || window.iJewelActiveViewer);
};

function debugWarn(message, error) {
  if (window.iJewelDebugConfig) console.warn('iJewel 3D: ' + message, error);
}

function isMobileViewport() {
  return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
}

function getOrbitControls(viewer) {
  const cam = viewer?.scene?.activeCamera || viewer?.getManager?.()?.activeCamera;
  if (!cam) return null;
  return typeof cam.getControls === 'function' ? cam.getControls() : cam.controls ?? null;
}

/* Authored camera pose per viewer: { target: {x,y,z}, distance }.
   Captured on ijewel:model-swap-staged — see the boot wiring for why that is
   the only readable window — and used as the anchor for every bound below. */
const authoredPoses = new WeakMap();

function captureAuthoredPose(viewer) {
  const controls = getOrbitControls(viewer);
  const cam = viewer?.scene?.activeCamera;
  const target = controls?.target;
  const position = controls?.object?.position || cam?.position;
  if (!target || !position) return;

  const distance = Math.hypot(
    position.x - target.x,
    position.y - target.y,
    position.z - target.z
  );
  if (!isFinite(distance) || distance <= 0) return;

  authoredPoses.set(viewer, {
    target: { x: target.x, y: target.y, z: target.z },
    distance,
  });
}

// Centre + radius of the sphere the orbit target is allowed to move in.
// Centre = bbox centre (the model sits centred in its own allowed region).
// Radius = the model's largest half-extent, floored so a GLB whose authored
// framing sits outside its own bounds still starts with headroom.
function computeTargetBound(pose, bounds) {
  const floor = pose.distance * TARGET_RADIUS_FLOOR_RATIO;
  if (!bounds?.min || !bounds?.max) {
    return { centre: pose.target, radius: floor };
  }

  const centre = {
    x: (bounds.min.x + bounds.max.x) / 2,
    y: (bounds.min.y + bounds.max.y) / 2,
    z: (bounds.min.z + bounds.max.z) / 2,
  };
  // Largest half-extent, not the half-diagonal: the diagonal overshoots the
  // model's own span along every axis, which is what lets a thin piece slip
  // out of frame at deep zoom.
  const maxHalfExtent =
    Math.max(
      bounds.max.x - bounds.min.x,
      bounds.max.y - bounds.min.y,
      bounds.max.z - bounds.min.z
    ) / 2;
  const authoredOffset = Math.hypot(
    pose.target.x - centre.x,
    pose.target.y - centre.y,
    pose.target.z - centre.z
  );

  const radius = Math.max(
    maxHalfExtent * TARGET_RADIUS_MARGIN,
    authoredOffset * AUTHORED_OFFSET_MARGIN,
    floor
  );
  if (!isFinite(radius) || radius <= 0) return { centre, radius: floor };
  return { centre, radius };
}

/* Zoom-to-cursor and panning both on, bounded by native OrbitControls
   constraints only:

   - `cursor` + `maxTargetRadius`: the SDK clamps the orbit target into a
     sphere around `cursor` on every update (target.sub(cursor).clampLength(
     minTargetRadius, maxTargetRadius).add(cursor)).

     NAMING TRAP: `cursor` is NOT the pointer. In the WebGI/three controls it
     appears in exactly two places — its own construction and that clampLength
     — and nowhere in the zoom path. The live zoom focus stays where iJewel
     puts it: `_mouse`/`_dollyDirection`, recomputed from the real pointer
     position on every wheel event. `cursor` here is only the CENTRE OF THE
     SAFETY SPHERE — see computeTargetBound for how it is sized, and why the
     model's bbox centre beats the authored target as that centre.

     This is also why maxTargetRadius alone is a sufficient safety bound: the
     SDK rebuilds the camera position every update as target + spherical
     offset and looks at the target. Bound the target and the camera is
     bounded with it — the piece can never be more than roughly one model
     extent from the centre of frame.

   - `minDistance`/`maxDistance` off the authored rest distance.

   `clampMin`/`clampMax` are deliberately NOT used: the SDK applies that same
   AABB to the camera POSITION as well as the target, so any box tight enough
   to bound the target would also trap the camera inside the model.

   Re-applied after every model load because each GLB import builds fresh
   OrbitControls at the SDK/GLB defaults (pan on, no bounds). */
function applyInteractionPolicy(viewer) {
  const controls = getOrbitControls(viewer);
  if (!controls) return;

  controls.zoomToCursor = true;
  controls.enablePan = true;

  const pose = authoredPoses.get(viewer);
  // No anchor yet (controls exist before the first model stages) — leave the
  // SDK defaults rather than bounding against a pose we have not read. A
  // later model-ready/swap-done re-apply installs the bounds.
  if (!pose) return;

  try {
    const bounds = viewer?.scene?.getModelBounds?.();

    if (controls.cursor && typeof controls.cursor.set === 'function') {
      const bound = computeTargetBound(pose, bounds);
      controls.cursor.set(bound.centre.x, bound.centre.y, bound.centre.z);
      controls.minTargetRadius = 0;
      controls.maxTargetRadius = bound.radius;
    } else {
      // Without `cursor` the radius would clamp around the world origin, which
      // is wrong for every pendant-framed GLB. Better unbounded than mis-bounded.
      debugWarn('OrbitControls.cursor unavailable — target radius not applied');
    }

    controls.minDistance = pose.distance * MIN_DISTANCE_RATIO;
    controls.maxDistance = pose.distance * MAX_DISTANCE_RATIO;
  } catch (error) {
    debugWarn('interaction policy failed', error);
  }
}
